import { useMemo, useState } from 'react';
import { AdvancedFilterButton, FilterPanelCollapse } from '@/components/common/AdvancedFilter';
import { DataTable } from '@/components/common/DataTable';
import { DateRangeBadge } from '@/components/common/DateRangeBadge';
import { ErrorState } from '@/components/common/ErrorState';
import { StatCardGrid } from '@/components/common/StatCardGrid';
import { PageHeader } from '@/components/layout/PageHeader';
import { FIT_TO_SCREEN_PAGE_CLASS } from '@/components/layout/page.constants';
import { getRouteApi } from '@tanstack/react-router';
import { useInfiniteTableQuery } from '@/hooks/useInfiniteTableQuery';
import { requisitionReviewInfiniteQueryOptions } from '../api/productRequisition.api';
import { RequisitionDecisionModal } from '../components/RequisitionDecisionModal';
import { RequisitionFilterPanel } from '../components/RequisitionFilterPanel';
import { getRequisitionReviewColumns } from '../components/RequisitionColumns';
import { useRequisitionFilters } from '../hooks/useRequisitionFilters';
import { useSpecimenFilterField } from '@/features/product/hooks/useSpecimenFilterField';
import type {
  RequisitionReviewSearch,
  SpecimenRequisitionRow,
} from '../types/productRequisition.types';

const route = getRouteApi('/_auth/product/specimen-requisition-review');
const SEARCH_PLACEHOLDER = 'Institute or specimen';

const NUMERIC_KEYS = ['specimenId', 'instituteTypeId', 'thanaId'] as const;

const STAGE_FIELD = {
  type: 'select' as const,
  key: 'isReview',
  label: 'Review Stage',
  options: [
    { value: 'false', label: 'Waiting for review' },
    { value: 'true', label: 'Reviewed' },
  ],
};

export function RequisitionReviewPage() {
  const routeSearch = route.useSearch();
  const routeNavigate = route.useNavigate();
  const controller = useRequisitionFilters({
    search: routeSearch,
    numericKeys: NUMERIC_KEYS,
    onApplyFilters: (next) =>
      void routeNavigate({ search: (previous) => ({ ...previous, ...next }), replace: true }),
    onApplySearch: (text) =>
      void routeNavigate({ search: (previous) => ({ ...previous, search: text }), replace: true }),
    withDateRange: true,
  });
  const params = controller.params as RequisitionReviewSearch;

  const { rows, firstPageMeta, isLoading, loadError, refetch, loadMoreProps } =
    useInfiniteTableQuery(requisitionReviewInfiniteQueryOptions(params));

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [deciding, setDeciding] = useState<SpecimenRequisitionRow | null>(null);
  const specimenField = useSpecimenFilterField(controller.filterValues.specimenId ?? '');

  const columns = useMemo(
    () => getRequisitionReviewColumns(params.search, setDeciding),
    [params.search],
  );
  const totals = firstPageMeta?.totals;

  const stats = [
    { key: 'required', label: 'Requisition Qty', value: totals?.required },
    { key: 'teacher', label: 'Teacher Qty', value: totals?.teacher },
    { key: 'targetSale', label: 'Target Sale Qty', value: totals?.targetSale },
    { key: 'rows', label: 'Requisitions', value: firstPageMeta?.total },
  ];

  return (
    <div className={FIT_TO_SCREEN_PAGE_CLASS}>
      <PageHeader
        title="Specimen Requisition Review"
        titleAddon={
          <DateRangeBadge
            onClick={() => setIsFilterOpen(true)}
            dateFrom={params.dateFrom}
            dateTo={params.dateTo}
          />
        }
        search={{
          value: controller.searchText,
          onChange: controller.setSearchText,
          placeholder: SEARCH_PLACEHOLDER,
        }}
        actions={
          <AdvancedFilterButton
            isOpen={isFilterOpen}
            activeCount={controller.activeFilterCount}
            onClick={() => setIsFilterOpen((wasOpen) => !wasOpen)}
          />
        }
      />

      <FilterPanelCollapse isOpen={isFilterOpen}>
        <RequisitionFilterPanel
          filters={controller}
          pageFields={[specimenField, STAGE_FIELD]}
          withDateRange
          withGeo
          withInstituteType
        />
      </FilterPanelCollapse>

      <StatCardGrid stats={stats} isLoading={isLoading} isError={loadError !== null} />

      {loadError ? (
        <ErrorState
          error={loadError}
          fallbackTitle="Could not load the requisitions"
          onRetry={() => void refetch()}
          fill
        />
      ) : (
        <DataTable
          data={rows}
          columns={columns}
          isLoading={isLoading}
          className="lg:flex-1"
          stickyColumnId="instituteName"
          emptyMessage="No requisition is waiting"
          {...loadMoreProps}
        />
      )}

      <RequisitionDecisionModal row={deciding} onClose={() => setDeciding(null)} />
    </div>
  );
}
